import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Button,
  Card,
  CardContent,
  Stack,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Divider,
  Alert,
  CircularProgress,
} from '@mui/material';
import {
  AttachMoney,
  TrendingUp,
  Remove,
  Visibility,
  FilterList,
  Download,
} from '@mui/icons-material';
import { FormControl, InputLabel, Select, MenuItem } from '@mui/material';
import { supabase } from '../../supabaseClient';
import { useTranslation } from 'react-i18next';
import { useDarkMode, getClozedStyles } from '../../hooks/useDarkMode';
import { useAuth } from '../../components/AuthProvider';
import * as XLSX from 'xlsx';

interface WeeklySettlement {
  id: string;
  seller_id: string;
  settlement_type: 'sales' | 'affiliate';
  week_number: number;
  year: number;
  period_start_date: string;
  period_end_date: string;
  gross_sales: number;
  refund_amount: number;
  net_sales: number;
  commission_rate: number;
  commission_amount: number;
  payment_fee_amount: number; // 추가: 결제 수수료 금액
  final_amount: number;
  actual_payment_amount: number; // 실제 지급액 (0원 미만은 0으로 처리)
  carried_forward_amount: number; // 이월 금액 (마이너스 정산 시)
  status: 'pending' | 'processing' | 'completed' | 'cancelled';
  due_date: string;
  created_at: string;
}

interface SettlementDetail {
  product_name?: string;
  quantity?: number;
  unit_price?: number;
  total_amount: number;
  commission: number;
  net_amount?: number;
  order_date?: string;
  earned_at?: string;
  brand_name?: string;
  order_amount?: number;
  commission_rate?: number;
  commission_amount?: number;
  is_affiliate?: boolean;
  affiliate_commission_rate?: number | null;
  is_refund?: boolean; // 환불 항목 여부
  refund_date?: string; // 환불 처리일
  is_later_refunded?: boolean; // 정산 생성 이후 환불됨
  current_status?: string; // 현재 주문 상태
  is_reference_only?: boolean; // 참고용 (정산에 미적용)
  is_excluded?: boolean; // 제외된 주문 (같은 주 취소/환불)
  item_delivery_status?: string; // 배송 상태
}

export default function SellerSettlement() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const isDarkMode = useDarkMode();
  const styles = getClozedStyles(isDarkMode);
  const [salesSettlements, setSalesSettlements] = useState<WeeklySettlement[]>([]);
  const [affiliateSettlements, setAffiliateSettlements] = useState<WeeklySettlement[]>([]);
  const [loading, setLoading] = useState(true);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [selectedSettlement, setSelectedSettlement] = useState<WeeklySettlement | null>(null);
  const [settlementDetails, setSettlementDetails] = useState<SettlementDetail[]>([]);

  // 필터 상태
  const [yearFilter, setYearFilter] = useState<string>('all');
  const [weekFilter, setWeekFilter] = useState<string>('all');

  // 페이지네이션 상태
  const [salesPage, setSalesPage] = useState(0);
  const [salesRowsPerPage, setSalesRowsPerPage] = useState(10);
  const [affiliatePage, setAffiliatePage] = useState(0);
  const [affiliateRowsPerPage, setAffiliateRowsPerPage] = useState(10);

  useEffect(() => {
    loadSettlements();
  }, [user]);

  const loadSettlements = async () => {
    if (!user) {
      console.error('User not authenticated');
      return;
    }

    try {
      setLoading(true);

      // 셀러 프로필 조회
      const { data: sellerProfile } = await supabase
        .from('seller_profile')
        .select('id')
        .eq('user_id', user.id)
        .single();

      if (!sellerProfile) {
        console.error('Seller profile not found');
        return;
      }

      // 주간 정산 데이터 조회 (판매)
      const { data: salesData, error: salesError } = await supabase
        .from('weekly_settlements')
        .select('*')
        .eq('seller_id', sellerProfile.id)
        .eq('settlement_type', 'sales')
        .order('year', { ascending: false })
        .order('week_number', { ascending: false });

      if (salesError) {
        console.error('Error loading sales settlements:', salesError);
      } else {
        setSalesSettlements(salesData || []);
      }

      // 주간 정산 데이터 조회 (어필리에이트)
      const { data: affiliateData, error: affiliateError } = await supabase
        .from('weekly_settlements')
        .select('*')
        .eq('seller_id', sellerProfile.id)
        .eq('settlement_type', 'affiliate')
        .order('year', { ascending: false })
        .order('week_number', { ascending: false });

      if (affiliateError) {
        console.error('Error loading affiliate settlements:', affiliateError);
      } else {
        setAffiliateSettlements(affiliateData || []);
      }

    } catch (error) {
      console.error('Error loading settlements:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleViewDetail = async (settlement: WeeklySettlement) => {
    setSelectedSettlement(settlement);
    setSettlementDetails([]);

    try {
      if (settlement.settlement_type === 'sales') {
        // 판매 정산 상세 조회 (affiliate_commission_rate는 테이블에 직접 저장됨)
        const { data: salesItems, error } = await supabase
          .from('weekly_settlement_sales_items')
          .select('*')
          .eq('settlement_id', settlement.id)
          .order('order_date', { ascending: false});

        if (error) {
          console.error('Error loading sales items:', error);
          return;
        }

        // 환불 내역도 조회 - 해당 정산에 적용된 환불만 조회
        const { data: refundItems, error: refundError } = await supabase
          .from('weekly_settlement_refunds')
          .select(`
            *,
            order_items:order_item_id (
              product_name,
              product_price,
              quantity,
              total_price
            )
          `)
          .eq('applied_settlement_id', settlement.id)
          .order('refund_date', { ascending: false });

        if (refundError) {
          console.error('Error loading refund items:', refundError);
        }

        // 판매 항목의 order_item_id 수집 (현재 상태 확인용)
        const orderItemIds = (salesItems || []).map(item => item.order_item_id).filter(Boolean);

        // 해당 주차 기간에 주문되었지만 정산에 포함되지 않은 주문 조회
        const periodEndPlusOne = new Date(new Date(settlement.period_end_date).getTime() + 24 * 60 * 60 * 1000).toISOString();

        const { data: excludedOrders, error: excludedError } = await supabase
          .from('order_items')
          .select(`
            id,
            product_name,
            product_price,
            quantity,
            total_price,
            item_delivery_status,
            created_at,
            order:order_id (
              id,
              payment_status,
              created_at
            ),
            product:product_id (
              seller_id
            )
          `)
          .in('item_delivery_status', ['cancelled', 'refunded', 'returned'])
          .order('created_at', { ascending: false })
          .limit(1000);

        if (excludedError) {
          console.error('Error loading excluded orders:', excludedError);
        }

        // 클라이언트에서 seller_id와 기간 필터링
        const periodStartDate = new Date(settlement.period_start_date);
        const periodEndDate = new Date(periodEndPlusOne);

        const filteredExcludedOrders = (excludedOrders || []).filter((item: any) => {
          if (!item.product || item.product.seller_id !== settlement.seller_id) {
            return false;
          }
          const orderDate = new Date(Array.isArray(item.order) ? item.order[0]?.created_at : item.order?.created_at);
          return orderDate >= periodStartDate && orderDate < periodEndDate;
        });

        // 해당 order_item들의 현재 상태 및 환불 정보 조회
        let currentOrderItemsStatus: any[] = [];
        let laterRefundRecords: any[] = [];

        if (orderItemIds.length > 0) {
          const { data: orderItemsData } = await supabase
            .from('order_items')
            .select('id, item_delivery_status')
            .in('id', orderItemIds);
          currentOrderItemsStatus = orderItemsData || [];

          // 정산 이후 환불된 레코드 조회 (applied_settlement_id가 현재 정산이 아닌 것)
          const { data: laterRefunds } = await supabase
            .from('weekly_settlement_refunds')
            .select(`
              *,
              order_items:order_item_id (
                product_name,
                product_price,
                quantity,
                total_price
              )
            `)
            .in('order_item_id', orderItemIds)
            .neq('applied_settlement_id', settlement.id)
            .order('refund_date', { ascending: false });

          laterRefundRecords = laterRefunds || [];
        }

        // 현재 상태 맵 생성
        const statusMap = new Map(
          currentOrderItemsStatus.map(item => [item.id, item.item_delivery_status])
        );

        // 판매 항목에 현재 상태 추가
        const salesItemsWithFlag = (salesItems || []).map(item => {
          const currentStatus = statusMap.get(item.order_item_id);
          const isLaterRefunded = currentStatus === 'refunded' || currentStatus === 'returned';

          return {
            ...item,
            is_refund: false,
            is_later_refunded: isLaterRefunded, // 정산 생성 이후 환불됨
            current_status: currentStatus,
          };
        });

        // 환불 항목을 SettlementDetail 형식으로 변환 (정산에 적용된 환불)
        const refundItemsFormatted = (refundItems || []).map(item => ({
          product_name: item.order_items?.product_name || '알 수 없는 상품',
          quantity: item.order_items?.quantity || 1,
          unit_price: item.order_items?.product_price || 0,
          total_amount: item.refund_amount,
          commission: 0,
          net_amount: item.refund_amount,
          order_date: item.refund_date,
          refund_date: item.refund_date,
          is_affiliate: false,
          affiliate_commission_rate: null,
          is_refund: true,
          is_later_refunded: false,
          is_reference_only: false, // 정산에 적용된 환불
        }));

        // 정산 이후 환불된 레코드 (참고용)
        const laterRefundsFormatted = laterRefundRecords.map(item => ({
          product_name: item.order_items?.product_name || '알 수 없는 상품',
          quantity: item.order_items?.quantity || 1,
          unit_price: item.order_items?.product_price || 0,
          total_amount: item.refund_amount,
          commission: 0,
          net_amount: item.refund_amount,
          order_date: item.refund_date,
          refund_date: item.refund_date,
          is_affiliate: false,
          affiliate_commission_rate: null,
          is_refund: true,
          is_later_refunded: false,
          is_reference_only: true, // 참고용 (다른 주차 정산에 적용됨)
        }));

        // 제외된 주문 (같은 주에 취소/환불됨) - 참고용
        const excludedOrdersFormatted = filteredExcludedOrders.map((item: any) => ({
          product_name: item.product_name,
          quantity: item.quantity,
          unit_price: item.product_price,
          total_amount: item.total_price,
          commission: 0,
          net_amount: 0,
          order_date: (Array.isArray(item.order) ? item.order[0]?.created_at : item.order?.created_at) || item.created_at,
          is_affiliate: false,
          affiliate_commission_rate: null,
          is_refund: false,
          is_later_refunded: false,
          is_reference_only: true, // 참고용 (정산에서 제외됨)
          is_excluded: true, // 제외된 주문
          item_delivery_status: item.item_delivery_status,
        }));

        setSettlementDetails([...salesItemsWithFlag, ...refundItemsFormatted, ...laterRefundsFormatted, ...excludedOrdersFormatted]);
      } else {
        // 어필리에이트 정산 상세 조회 - brand_name은 테이블에 직접 저장됨
        const { data: affiliateItems, error } = await supabase
          .from('weekly_settlement_affiliate_items')
          .select('*')
          .eq('settlement_id', settlement.id)
          .order('earned_at', { ascending: false });

        if (error) {
          console.error('Error loading affiliate items:', error);
          return;
        }

        // 어필리에이트 환불 내역도 조회 - 해당 정산에 적용된 환불만 조회
        const { data: affiliateRefundItems, error: affiliateRefundError } = await supabase
          .from('weekly_settlement_affiliate_refunds')
          .select('*')
          .eq('applied_settlement_id', settlement.id)
          .order('refund_date', { ascending: false });

        if (affiliateRefundError) {
          console.error('Error loading affiliate refund items:', affiliateRefundError);
        }

        const formattedItems = (affiliateItems || []).map(item => ({
          product_name: item.product_name,
          brand_name: item.brand_name || '알 수 없는 브랜드',
          order_amount: item.order_amount,
          commission_rate: item.commission_rate,
          commission_amount: item.commission_amount,
          total_amount: item.order_amount,
          commission: item.commission_amount,
          earned_at: item.earned_at,
          is_refund: false,
        }));

        // 어필리에이트 환불 항목 변환 (이제 product_name, brand_name이 테이블에 직접 있음)
        const affiliateRefundFormatted = (affiliateRefundItems || []).map(item => ({
          product_name: item.product_name || '알 수 없는 상품',
          brand_name: item.brand_name || '알 수 없는 브랜드',
          order_amount: item.refund_amount, // 환불 금액을 주문 금액으로 표시
          commission_rate: 0,
          commission_amount: item.refund_amount,
          total_amount: item.refund_amount,
          commission: item.refund_amount,
          earned_at: item.refund_date,
          refund_date: item.refund_date,
          is_refund: true,
        }));

        setSettlementDetails([...formattedItems, ...affiliateRefundFormatted]);
      }
    } catch (error) {
      console.error('Error loading settlement details:', error);
    }

    setDetailDialogOpen(true);
  };

  const handleExportSettlementDetailToExcel = () => {
    if (!selectedSettlement) return;

    const isSales = selectedSettlement.settlement_type === 'sales';

    // 엑셀 다운로드용 데이터 준비
    const excelData = settlementDetails.map((detail, index) => {
      const isAffiliate = detail.is_affiliate || false;
      const isRefund = detail.is_refund || false;
      const isLaterRefunded = detail.is_later_refunded || false;
      const isReferenceOnly = detail.is_reference_only || false;
      const isExcluded = detail.is_excluded || false;
      const baseCommissionRate = selectedSettlement?.commission_rate || 10;
      const affiliateCommissionRate = detail.affiliate_commission_rate || 0;
      const paymentFee = detail.total_amount * 0.036;
      const affiliateCommission = isAffiliate ? detail.total_amount * (affiliateCommissionRate / 100) : 0;

      if (isSales) {
        // 판매 정산
        let status = '';
        if (isRefund && !isReferenceOnly) status = '환불';
        else if (isReferenceOnly && !isExcluded) status = '참고용 (다른 주차 정산 적용)';
        else if (isExcluded) status = `참고용 (${detail.item_delivery_status === 'cancelled' ? '취소' : '같은 주 환불'})`;
        else if (isLaterRefunded) status = '이후 환불됨';
        else status = '정상';

        return {
          '번호': index + 1,
          '상태': status,
          '상품명': detail.product_name,
          'Affiliate 주문': isAffiliate ? 'Y' : 'N',
          '수량': isRefund ? `-${detail.quantity}` : detail.quantity,
          '단가': detail.unit_price || 0,
          '총액': detail.total_amount,
          '플랫폼 수수료율': isRefund ? '-' : `${baseCommissionRate}%`,
          '플랫폼 수수료': isRefund ? 0 : detail.commission,
          '어필리에이트 수수료율': isRefund ? '-' : isAffiliate ? `${affiliateCommissionRate}%` : '-',
          '어필리에이트 수수료': isRefund ? 0 : isAffiliate ? Math.round(affiliateCommission) : 0,
          '결제 수수료율': isRefund ? '-' : '3.6%',
          '결제 수수료': isRefund ? 0 : Math.round(paymentFee),
          '정산금액': isReferenceOnly ? '0 (참고용)' : Math.abs(detail.net_amount || 0),
          '주문일/환불일': isRefund
            ? (detail.refund_date ? formatDate(detail.refund_date) : '-')
            : (detail.order_date ? formatDate(detail.order_date) : '-'),
        };
      } else {
        // 어필리에이트 정산
        return {
          '번호': index + 1,
          '상태': isRefund ? '환불' : '정상',
          '상품명': detail.product_name,
          '브랜드': detail.brand_name,
          '주문금액': detail.order_amount || 0,
          '크리에이터 수수료율': `${detail.commission_rate}%`,
          '받은 커미션': detail.commission_amount || 0,
          '수익 발생일': isRefund
            ? (detail.refund_date ? formatDate(detail.refund_date) : '-')
            : (detail.earned_at ? formatDate(detail.earned_at) : '-'),
        };
      }
    });

    // 워크시트 생성
    const ws = XLSX.utils.json_to_sheet(excelData);

    // 컬럼 너비 설정
    const colWidths = isSales ? [
      { wch: 6 },  // 번호
      { wch: 25 }, // 상태
      { wch: 30 }, // 상품명
      { wch: 12 }, // Affiliate 주문
      { wch: 8 },  // 수량
      { wch: 12 }, // 단가
      { wch: 12 }, // 총액
      { wch: 15 }, // 플랫폼 수수료율
      { wch: 15 }, // 플랫폼 수수료
      { wch: 18 }, // 어필리에이트 수수료율
      { wch: 18 }, // 어필리에이트 수수료
      { wch: 15 }, // 결제 수수료율
      { wch: 15 }, // 결제 수수료
      { wch: 15 }, // 정산금액
      { wch: 15 }, // 주문일/환불일
    ] : [
      { wch: 6 },  // 번호
      { wch: 10 }, // 상태
      { wch: 30 }, // 상품명
      { wch: 20 }, // 브랜드
      { wch: 15 }, // 주문금액
      { wch: 18 }, // 크리에이터 수수료율
      { wch: 15 }, // 받은 커미션
      { wch: 15 }, // 수익 발생일
    ];
    ws['!cols'] = colWidths;

    // 워크북 생성
    const wb = XLSX.utils.book_new();

    // 정산 요약 정보 시트 추가
    const summaryData = [
      { '항목': '정산 유형', '내용': isSales ? '판매 정산' : '어필리에이트 정산' },
      { '항목': '정산 기간', '내용': `${selectedSettlement.year}년 ${selectedSettlement.week_number}주` },
      { '항목': '기간', '내용': `${formatDate(selectedSettlement.period_start_date)} ~ ${formatDate(selectedSettlement.period_end_date)}` },
      { '항목': '총 매출', '내용': `₩${selectedSettlement.gross_sales.toLocaleString()}` },
      { '항목': '환불 차감', '내용': `-₩${selectedSettlement.refund_amount.toLocaleString()}` },
      { '항목': '순 매출', '내용': `₩${selectedSettlement.net_sales.toLocaleString()}` },
    ];

    if (isSales) {
      summaryData.push(
        { '항목': '플랫폼 수수료', '내용': `-₩${selectedSettlement.commission_amount.toLocaleString()} (${selectedSettlement.commission_rate}%)` },
        { '항목': '결제 수수료', '내용': `-₩${(selectedSettlement.payment_fee_amount || 0).toLocaleString()} (3.6%)` }
      );
    }

    summaryData.push(
      { '항목': '최종 정산 금액', '내용': `₩${selectedSettlement.final_amount.toLocaleString()}` }
    );

    const wsSummary = XLSX.utils.json_to_sheet(summaryData);
    wsSummary['!cols'] = [{ wch: 15 }, { wch: 40 }];

    XLSX.utils.book_append_sheet(wb, wsSummary, '정산 요약');
    XLSX.utils.book_append_sheet(wb, ws, '상세 내역');

    // 파일명 생성
    const today = new Date();
    const settlementType = isSales ? '판매정산' : '어필리에이트정산';
    const fileName = `${settlementType}_${selectedSettlement.year}년${selectedSettlement.week_number}주_${today.getFullYear()}${String(today.getMonth() + 1).padStart(2, '0')}${String(today.getDate()).padStart(2, '0')}.xlsx`;

    // 파일 다운로드
    XLSX.writeFile(wb, fileName);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed': return 'success';
      case 'processing': return 'warning';
      case 'pending': return 'info';
      case 'cancelled': return 'error';
      default: return 'default';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'completed': return t('sellerSettlement.completedStatus');
      case 'processing': return t('sellerSettlement.processingStatus');
      case 'pending': return t('sellerSettlement.pendingStatus');
      case 'cancelled': return t('sellerSettlement.cancelledStatus');
      default: return status;
    }
  };

  const formatDate = (dateString: string) => {
    // DB에 저장된 시간이 이미 KST이므로 UTC 부분만 제거하고 표시
    const date = new Date(dateString);
    // toISOString으로 UTC 시간 추출 후 그대로 표시 (DB값이 이미 KST로 저장됨)
    return `${date.getUTCMonth() + 1}월 ${date.getUTCDate()}일`;
  };

  // 필터링 로직
  const filterSettlements = (settlements: WeeklySettlement[]) => {
    return settlements.filter(settlement => {
      const yearMatch = yearFilter === 'all' || settlement.year.toString() === yearFilter;
      const weekMatch = weekFilter === 'all' || settlement.week_number.toString() === weekFilter;
      return yearMatch && weekMatch;
    });
  };

  const filteredSalesSettlements = filterSettlements(salesSettlements);
  const filteredAffiliateSettlements = filterSettlements(affiliateSettlements);

  // 정렬된 데이터
  const sortedSalesSettlements = [...filteredSalesSettlements].sort((a, b) => {
    if (a.year !== b.year) return b.year - a.year;
    return b.week_number - a.week_number;
  });

  const sortedAffiliateSettlements = [...filteredAffiliateSettlements].sort((a, b) => {
    if (a.year !== b.year) return b.year - a.year;
    return b.week_number - a.week_number;
  });

  // 페이지네이션 적용
  const paginatedSalesSettlements = sortedSalesSettlements.slice(
    salesPage * salesRowsPerPage,
    salesPage * salesRowsPerPage + salesRowsPerPage
  );

  const paginatedAffiliateSettlements = sortedAffiliateSettlements.slice(
    affiliatePage * affiliateRowsPerPage,
    affiliatePage * affiliateRowsPerPage + affiliateRowsPerPage
  );

  // 페이지네이션 핸들러
  const handleSalesPageChange = (event: unknown, newPage: number) => {
    setSalesPage(newPage);
  };

  const handleSalesRowsPerPageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSalesRowsPerPage(parseInt(event.target.value, 10));
    setSalesPage(0);
  };

  const handleAffiliatePageChange = (event: unknown, newPage: number) => {
    setAffiliatePage(newPage);
  };

  const handleAffiliateRowsPerPageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setAffiliateRowsPerPage(parseInt(event.target.value, 10));
    setAffiliatePage(0);
  };

  // 사용 가능한 년도 및 주차 목록 생성
  const allSettlements = [...salesSettlements, ...affiliateSettlements];
  const availableYears = Array.from(new Set(allSettlements.map(s => s.year))).sort((a, b) => b - a);
  const availableWeeks = Array.from(new Set(allSettlements.map(s => s.week_number))).sort((a, b) => a - b);

  const totalSalesAmount = filteredSalesSettlements.reduce((sum, s) => sum + s.final_amount, 0);
  const totalAffiliateAmount = filteredAffiliateSettlements.reduce((sum, s) => sum + s.final_amount, 0);
  const totalRefunds = filteredSalesSettlements.reduce((sum, s) => sum + s.refund_amount, 0);

  if (loading) {
    return (
      <Box sx={{ ...styles.pageContainer, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={styles.pageContainer}>
      <Typography variant="h4" sx={{ ...styles.title, fontWeight: 'bold', mb: 3 }}>
        {t('sellerSettlement.title')}
      </Typography>

      <Alert severity="info" sx={{ mb: 3 }}>
        <Typography variant="body2" gutterBottom>
          • {t('sellerSettlement.autoGenInfo')}
        </Typography>
        <Typography variant="body2" gutterBottom>
          • {t('sellerSettlement.refundDeductInfo')}
        </Typography>
      </Alert>

      {/* 필터 */}
      <Paper sx={{ ...styles.paper, p: 2, mb: 3 }}>
        <Stack direction="row" spacing={2} alignItems="center">
          <FilterList color="action" />
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel sx={styles.text}>{t('sellerSettlement.year')}</InputLabel>
            <Select
              value={yearFilter}
              label={t('sellerSettlement.year')}
              onChange={(e) => setYearFilter(e.target.value)}
              sx={styles.input}
              MenuProps={{
                PaperProps: {
                  sx: styles.selectMenu
                }
              }}
            >
              <MenuItem value="all" sx={styles.menuItem}>{t('sellerSettlement.all')}</MenuItem>
              {availableYears.map(year => (
                <MenuItem key={year} value={year.toString()} sx={styles.menuItem}>{year}{t('sellerSettlement.yearUnit')}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel sx={styles.text}>{t('sellerSettlement.week')}</InputLabel>
            <Select
              value={weekFilter}
              label={t('sellerSettlement.week')}
              onChange={(e) => setWeekFilter(e.target.value)}
              sx={styles.input}
              MenuProps={{
                PaperProps: {
                  sx: styles.selectMenu
                }
              }}
            >
              <MenuItem value="all" sx={styles.menuItem}>{t('sellerSettlement.all')}</MenuItem>
              {availableWeeks.map(week => (
                <MenuItem key={week} value={week.toString()} sx={styles.menuItem}>{week}{t('sellerSettlement.weekUnit')}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button
            variant="outlined"
            size="small"
            onClick={() => {
              setYearFilter('all');
              setWeekFilter('all');
            }}
          >
            {t('sellerSettlement.filterReset')}
          </Button>
        </Stack>
      </Paper>

      {/* 요약 카드 */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={4}>
          <Card sx={styles.paper}>
            <CardContent>
              <Stack direction="row" alignItems="center" spacing={1}>
                <AttachMoney color="primary" fontSize="large" />
                <Box sx={{ flex: 1 }}>
                  <Typography variant="body2" color="text.secondary" sx={styles.text}>
                    {t('sellerSettlement.salesSettlementTotal')}
                  </Typography>
                  <Typography variant="h5" color="primary" sx={{ ...styles.text, fontWeight: 'bold' }}>
                    ₩{totalSalesAmount.toLocaleString()}
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card sx={styles.paper}>
            <CardContent>
              <Stack direction="row" alignItems="center" spacing={1}>
                <TrendingUp color="success" fontSize="large" />
                <Box sx={{ flex: 1 }}>
                  <Typography variant="body2" color="text.secondary" sx={styles.text}>
                    {t('sellerSettlement.affiliateTotal')}
                  </Typography>
                  <Typography variant="h5" color="success.main" sx={{ ...styles.text, fontWeight: 'bold' }}>
                    ₩{totalAffiliateAmount.toLocaleString()}
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card sx={styles.paper}>
            <CardContent>
              <Stack direction="row" alignItems="center" spacing={1}>
                <Remove color="error" fontSize="large" />
                <Box sx={{ flex: 1 }}>
                  <Typography variant="body2" color="text.secondary" sx={styles.text}>
                    {t('sellerSettlement.cumulativeRefundDeduction')}
                  </Typography>
                  <Typography variant="h5" color="error.main" sx={{ ...styles.text, fontWeight: 'bold' }}>
                    ₩{totalRefunds.toLocaleString()}
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* 판매 정산 테이블 */}
      <Paper sx={{ ...styles.paper, p: 3, mb: 3 }}>
        <Typography variant="h6" sx={{ ...styles.text, fontWeight: 'bold', mb: 2 }}>
          {t('sellerSettlement.salesSettlement')}
        </Typography>
        <TableContainer sx={styles.tableContainer}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>{t('sellerSettlement.division')}</TableCell>
                <TableCell>{t('sellerSettlement.year')}</TableCell>
                <TableCell>{t('sellerSettlement.week')}</TableCell>
                <TableCell>{t('sellerSettlement.period')}</TableCell>
                <TableCell align="right">{t('sellerSettlement.totalSales')}</TableCell>
                <TableCell align="right">{t('sellerSettlement.deductionRefund')}</TableCell>
                <TableCell align="right">{t('sellerSettlement.finalAmount')}</TableCell>
                <TableCell>{t('sellerSettlement.state')}</TableCell>
                <TableCell align="center">{t('sellerSettlement.actions')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredSalesSettlements.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} align="center">
                    <Typography color="text.secondary" sx={styles.text}>{t('sellerSettlement.noSettlements')}</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                paginatedSalesSettlements.map((settlement) => (
                  <TableRow key={settlement.id} hover>
                    <TableCell>{t('sellerSettlement.sales')}</TableCell>
                    <TableCell>{settlement.year}{t('sellerSettlement.yearUnit')}</TableCell>
                    <TableCell>{settlement.week_number}{t('sellerSettlement.weekUnit')}</TableCell>
                    <TableCell>
                      {formatDate(settlement.period_start_date)} ~ {formatDate(settlement.period_end_date)}
                    </TableCell>
                    <TableCell align="right">
                      ₩{settlement.gross_sales.toLocaleString()}
                    </TableCell>
                    <TableCell align="right">
                      <Typography color="error" sx={styles.text}>
                        ₩{settlement.refund_amount.toLocaleString()}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" fontWeight="bold" color="primary" sx={styles.text}>
                        ₩{settlement.final_amount.toLocaleString()}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={getStatusText(settlement.status)}
                        color={getStatusColor(settlement.status) as any}
                        size="small"
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Button
                        size="small"
                        startIcon={<Visibility />}
                        onClick={() => handleViewDetail(settlement)}
                      >
                        {t('sellerSettlement.viewDetail')}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={filteredSalesSettlements.length}
          page={salesPage}
          onPageChange={handleSalesPageChange}
          rowsPerPage={salesRowsPerPage}
          onRowsPerPageChange={handleSalesRowsPerPageChange}
          rowsPerPageOptions={[5, 10, 25, 50]}
          labelRowsPerPage={t('sellerSettlement.rowsPerPage')}
          labelDisplayedRows={({ from, to, count }) => `${from}-${to} / ${count}`}
        />
      </Paper>

      {/* 어필리에이트 정산 테이블 */}
      <Paper sx={{ ...styles.paper, p: 3 }}>
        <Typography variant="h6" sx={{ ...styles.text, fontWeight: 'bold', mb: 2 }}>
          {t('sellerSettlement.affiliateSettlement')}
        </Typography>
        <TableContainer sx={styles.tableContainer}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>{t('sellerSettlement.division')}</TableCell>
                <TableCell>{t('sellerSettlement.year')}</TableCell>
                <TableCell>{t('sellerSettlement.week')}</TableCell>
                <TableCell>{t('sellerSettlement.period')}</TableCell>
                <TableCell align="right">{t('sellerSettlement.totalSales')}</TableCell>
                <TableCell align="right">{t('sellerSettlement.deductionRefund')}</TableCell>
                <TableCell align="right">{t('sellerSettlement.finalAmount')}</TableCell>
                <TableCell>{t('sellerSettlement.state')}</TableCell>
                <TableCell align="center">{t('sellerSettlement.actions')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredAffiliateSettlements.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} align="center">
                    <Typography color="text.secondary" sx={styles.text}>정산 내역이 없습니다.</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                paginatedAffiliateSettlements.map((settlement) => (
                  <TableRow key={settlement.id} hover>
                    <TableCell>{t('sellerSettlement.affiliate')}</TableCell>
                    <TableCell>{settlement.year}{t('sellerSettlement.yearUnit')}</TableCell>
                    <TableCell>{settlement.week_number}{t('sellerSettlement.weekUnit')}</TableCell>
                    <TableCell>
                      {formatDate(settlement.period_start_date)} ~ {formatDate(settlement.period_end_date)}
                    </TableCell>
                    <TableCell align="right">
                      ₩{settlement.gross_sales.toLocaleString()}
                    </TableCell>
                    <TableCell align="right">
                      <Typography color="error" sx={styles.text}>
                        ₩{settlement.refund_amount.toLocaleString()}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" fontWeight="bold" color="success.main" sx={styles.text}>
                        ₩{settlement.final_amount.toLocaleString()}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={getStatusText(settlement.status)}
                        color={getStatusColor(settlement.status) as any}
                        size="small"
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Button
                        size="small"
                        startIcon={<Visibility />}
                        onClick={() => handleViewDetail(settlement)}
                      >
                        {t('sellerSettlement.viewDetail')}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={filteredAffiliateSettlements.length}
          page={affiliatePage}
          onPageChange={handleAffiliatePageChange}
          rowsPerPage={affiliateRowsPerPage}
          onRowsPerPageChange={handleAffiliateRowsPerPageChange}
          rowsPerPageOptions={[5, 10, 25, 50]}
          labelRowsPerPage={t('sellerSettlement.rowsPerPage')}
          labelDisplayedRows={({ from, to, count }) => `${from}-${to} / ${count}`}
        />
      </Paper>

      {/* 상세 보기 다이얼로그 */}
      <Dialog
        open={detailDialogOpen}
        onClose={() => setDetailDialogOpen(false)}
        maxWidth="xl"
        fullWidth
        PaperProps={{ sx: { ...styles.paper, minWidth: '1200px' } }}
      >
        <DialogTitle sx={styles.text}>
          {t('sellerSettlement.settlementDetail')}
        </DialogTitle>
        <Alert severity="info" sx={{ mb: 3 }}>
          <Typography variant="body2" gutterBottom>
            • {t('sellerSettlement.cancelBeforeShip')}
          </Typography>
          <Typography variant="body2" gutterBottom>
            • {t('sellerSettlement.sameWeekRefund')}
          </Typography>
          <Typography variant="body2" gutterBottom>
            • {t('sellerSettlement.settlementBasis')}
          </Typography>
        </Alert>
        <DialogContent>
          {selectedSettlement && (
            <Box>
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="text.secondary" sx={styles.text}>
                    {t('sellerSettlement.settlementType')}
                  </Typography>
                  <Typography variant="body1" sx={styles.text}>
                    {selectedSettlement.settlement_type === 'sales' ? t('sellerSettlement.sales') : t('sellerSettlement.affiliate')}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="text.secondary" sx={styles.text}>
                    {t('sellerSettlement.settlementPeriod')}
                  </Typography>
                  <Typography variant="body1" sx={styles.text}>
                    {selectedSettlement.year}{t('sellerSettlement.yearUnit')} {selectedSettlement.week_number}{t('sellerSettlement.weekUnit')}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="text.secondary" sx={styles.text}>
                    {t('sellerSettlement.periodRange')}
                  </Typography>
                  <Typography variant="body1" sx={styles.text}>
                    {formatDate(selectedSettlement.period_start_date)} ~ {formatDate(selectedSettlement.period_end_date)}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="text.secondary" sx={styles.text}>
                    {t('sellerSettlement.grossSales')}
                  </Typography>
                  <Typography variant="body1" sx={styles.text}>
                    ₩{selectedSettlement.gross_sales.toLocaleString()}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="text.secondary" sx={styles.text}>
                    {t('sellerSettlement.refundDeduction')}
                  </Typography>
                  <Typography variant="body1" color="error" sx={styles.text}>
                    -₩{selectedSettlement.refund_amount.toLocaleString()}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="text.secondary" sx={styles.text}>
                    {t('sellerSettlement.netSales')}
                  </Typography>
                  <Typography variant="body1" sx={styles.text}>
                    ₩{selectedSettlement.net_sales.toLocaleString()}
                  </Typography>
                </Grid>
                {selectedSettlement.settlement_type === 'sales' && (
                  <>
                    <Grid item xs={6}>
                      <Typography variant="subtitle2" color="text.secondary" sx={styles.text}>
                        {t('sellerSettlement.platformFee')} ({selectedSettlement.commission_rate}%)
                      </Typography>
                      <Typography variant="body1" color="error" sx={styles.text}>
                        -₩{selectedSettlement.commission_amount.toLocaleString()}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="subtitle2" color="text.secondary" sx={styles.text}>
                        {t('sellerSettlement.paymentFee')} (3.6%)
                      </Typography>
                      <Typography variant="body1" color="error" sx={styles.text}>
                        -₩{(selectedSettlement.payment_fee_amount || 0).toLocaleString()}
                      </Typography>
                    </Grid>
                  </>
                )}
                <Grid item xs={12}>
                  <Divider sx={{ my: 1 }} />
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="text.secondary" sx={styles.text}>
                    {t('sellerSettlement.finalSettlement')}
                  </Typography>
                  <Typography variant="h6" color="primary" sx={styles.text}>
                    ₩{selectedSettlement.final_amount.toLocaleString()}
                  </Typography>
                </Grid>
              </Grid>

              <Divider sx={{ my: 2 }} />

              <Typography variant="h6" gutterBottom sx={styles.text}>
                {t('sellerSettlement.detailList')}
              </Typography>
              <TableContainer sx={styles.tableContainer}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      {selectedSettlement.settlement_type === 'sales' ? (
                        <>
                          <TableCell>{t('sellerSettlement.productName')}</TableCell>
                          <TableCell align="center">{t('sellerSettlement.isAffiliate')}</TableCell>
                          <TableCell align="right">{t('sellerSettlement.quantityLabel')}</TableCell>
                          <TableCell align="right">{t('sellerSettlement.unitPrice')}</TableCell>
                          <TableCell align="right">{t('sellerSettlement.totalAmount')}</TableCell>
                          <TableCell align="right">{t('sellerSettlement.platformFeeLabel')}</TableCell>
                          <TableCell align="right">{t('sellerSettlement.affiliateFee')}</TableCell>
                          <TableCell align="right">{t('sellerSettlement.paymentFeeLabel')}</TableCell>
                          <TableCell align="right">{t('sellerSettlement.settlementAmount')}</TableCell>
                          <TableCell>{t('sellerSettlement.orderDate')}</TableCell>
                        </>
                      ) : (
                        <>
                          <TableCell>{t('sellerSettlement.productName')}</TableCell>
                          <TableCell>{t('sellerSettlement.brand')}</TableCell>
                          <TableCell align="right">{t('sellerSettlement.orderAmount')}</TableCell>
                          <TableCell align="right">{t('sellerSettlement.creatorFeeRate')}</TableCell>
                          <TableCell align="right">{t('sellerSettlement.receivedCommission')}</TableCell>
                          <TableCell>{t('sellerSettlement.earningDate')}</TableCell>
                        </>
                      )}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {settlementDetails.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={10} align="center">
                          <Typography color="text.secondary" sx={styles.text}>{t('sellerSettlement.noDetailList')}</Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      settlementDetails.map((detail, index) => {
                        const isAffiliate = detail.is_affiliate || false;
                        const isRefund = detail.is_refund || false;
                        const isLaterRefunded = detail.is_later_refunded || false;
                        const isReferenceOnly = detail.is_reference_only || false;
                        const isExcluded = detail.is_excluded || false;
                        const baseCommissionRate = selectedSettlement?.commission_rate || 10;
                        const affiliateCommissionRate = detail.affiliate_commission_rate || 0;

                        // 결제 수수료 계산 (총액의 3.6%)
                        const paymentFee = detail.total_amount * 0.036;
                        // 어필리에이트 수수료 계산
                        const affiliateCommission = isAffiliate ? detail.total_amount * (affiliateCommissionRate / 100) : 0;

                        return (
                          <TableRow
                            key={index}
                            sx={isRefund && !isReferenceOnly ? {
                              backgroundColor: isDarkMode ? 'rgba(244, 67, 54, 0.15)' : 'rgba(244, 67, 54, 0.08)',
                            } : isReferenceOnly ? {
                              backgroundColor: isDarkMode ? 'rgba(158, 158, 158, 0.15)' : 'rgba(158, 158, 158, 0.08)',
                            } : isLaterRefunded ? {
                              backgroundColor: isDarkMode ? 'rgba(255, 152, 0, 0.15)' : 'rgba(255, 152, 0, 0.08)',
                            } : {}}
                          >
                            {selectedSettlement.settlement_type === 'sales' ? (
                              <>
                                <TableCell>
                                  <Stack direction="row" spacing={1} alignItems="center">
                                    {isRefund && !isReferenceOnly && (
                                      <Chip label={t('sellerSettlement.refundChip')} color="error" size="small" />
                                    )}
                                    {isReferenceOnly && !isExcluded && (
                                      <Chip label={t('sellerSettlement.referenceOtherWeek')} color="default" size="small" />
                                    )}
                                    {isExcluded && (
                                      <Chip
                                        label={`참고용 (${detail.item_delivery_status === 'cancelled' ? t('sellerSettlement.referenceCancelled') : t('sellerSettlement.referenceSameWeekRefund')})`}
                                        color="default"
                                        size="small"
                                      />
                                    )}
                                    {isLaterRefunded && (
                                      <Chip label={t('sellerSettlement.laterRefunded')} color="warning" size="small" />
                                    )}
                                    <Typography sx={styles.text}>{detail.product_name}</Typography>
                                  </Stack>
                                </TableCell>
                                <TableCell align="center">
                                  {isAffiliate ? (
                                    <Chip label="Y" color="success" size="small" />
                                  ) : (
                                    <Chip label="N" color="default" size="small" variant="outlined" />
                                  )}
                                </TableCell>
                                <TableCell align="right">{isRefund ? `-${detail.quantity}` : detail.quantity}</TableCell>
                                <TableCell align="right">₩{detail.unit_price?.toLocaleString()}</TableCell>
                                <TableCell align="right">
                                  <Typography color={isRefund ? 'error' : 'inherit'} sx={styles.text}>
                                    {isRefund ? '-' : ''}₩{detail.total_amount.toLocaleString()}
                                  </Typography>
                                </TableCell>
                                <TableCell align="right">
                                  {isRefund ? (
                                    <Typography variant="body2" color="text.secondary" sx={styles.text}>-</Typography>
                                  ) : (
                                    <Stack direction="column" alignItems="flex-end">
                                      <Typography variant="body2" sx={styles.text}>{baseCommissionRate}%</Typography>
                                      <Typography variant="body2" color="error" sx={styles.text}>
                                        -₩{detail.commission.toLocaleString()}
                                      </Typography>
                                    </Stack>
                                  )}
                                </TableCell>
                                <TableCell align="right">
                                  {isRefund ? (
                                    <Typography variant="body2" color="text.secondary" sx={styles.text}>-</Typography>
                                  ) : isAffiliate ? (
                                    <Stack direction="column" alignItems="flex-end">
                                      <Typography variant="body2" sx={styles.text}>{affiliateCommissionRate}%</Typography>
                                      <Typography variant="body2" color="error" sx={styles.text}>
                                        -₩{Math.round(affiliateCommission).toLocaleString()}
                                      </Typography>
                                    </Stack>
                                  ) : (
                                    <Typography variant="body2" color="text.secondary" sx={styles.text}>-</Typography>
                                  )}
                                </TableCell>
                                <TableCell align="right">
                                  {isRefund ? (
                                    <Typography variant="body2" color="text.secondary" sx={styles.text}>-</Typography>
                                  ) : (
                                    <Stack direction="column" alignItems="flex-end">
                                      <Typography variant="body2" sx={styles.text}>3.6%</Typography>
                                      <Typography variant="body2" color="error" sx={styles.text}>
                                        -₩{Math.round(paymentFee).toLocaleString()}
                                      </Typography>
                                    </Stack>
                                  )}
                                </TableCell>
                                <TableCell align="right">
                                  <Typography
                                    variant="body2"
                                    fontWeight="bold"
                                    color={isReferenceOnly ? 'text.secondary' : isRefund ? 'error' : 'inherit'}
                                    sx={styles.text}
                                  >
                                    {isReferenceOnly ? t('sellerSettlement.referenceNote') : (
                                      <>
                                        {isRefund ? '-' : ''}₩{Math.abs(detail.net_amount || 0).toLocaleString()}
                                      </>
                                    )}
                                  </Typography>
                                </TableCell>
                                <TableCell>
                                  {isRefund
                                    ? (detail.refund_date ? formatDate(detail.refund_date) : '-')
                                    : (detail.order_date ? formatDate(detail.order_date) : '-')
                                  }
                                </TableCell>
                              </>
                            ) : (
                            <>
                              <TableCell>
                                <Stack direction="row" spacing={1} alignItems="center">
                                  {isRefund && (
                                    <Chip label={t('sellerSettlement.refundChip')} color="error" size="small" />
                                  )}
                                  <Typography sx={styles.text}>{detail.product_name}</Typography>
                                </Stack>
                              </TableCell>
                              <TableCell>{detail.brand_name}</TableCell>
                              <TableCell align="right">
                                <Typography color={isRefund ? 'error' : 'inherit'} sx={styles.text}>
                                  {isRefund ? '-' : ''}₩{detail.order_amount?.toLocaleString()}
                                </Typography>
                              </TableCell>
                              <TableCell align="right">{detail.commission_rate}%</TableCell>
                              <TableCell align="right">
                                <Typography color={isRefund ? 'error' : 'inherit'} sx={styles.text}>
                                  {isRefund ? '-' : ''}₩{detail.commission_amount?.toLocaleString()}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                {isRefund
                                  ? (detail.refund_date ? formatDate(detail.refund_date) : '-')
                                  : (detail.earned_at ? formatDate(detail.earned_at) : '-')
                                }
                              </TableCell>
                            </>
                          )}
                        </TableRow>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailDialogOpen(false)}>{t("common.close")}</Button>
          <Button
            variant="contained"
            startIcon={<Download />}
            onClick={handleExportSettlementDetailToExcel}
            disabled={settlementDetails.length === 0}
          >
            {t('sellerSettlement.excelDownload')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
